import React, { useEffect, useMemo, useRef, useState } from "react";
import { createChart, BaselineSeries, LineSeries } from "lightweight-charts";

/**
 * PortfolioPLMiniChart (final + ideal line)
 * - Baseline (%): REAL (pct01) verde/vermelho
 * - Line (%): IDEAL (ideal_pct01)
 * - ✅ Eixo X respeita o range (syncedTimeRange/período)
 * - ✅ Curva só aparece a partir de openedDateISO (antes vira whitespace)
 */
export default function PortfolioPLMiniChart({
  title = "Retorno Acumulado (%)",
  data,
  todayPoint = null,
  openedDateISO = null, // ✅ NOVO (YYYY-MM-DD)
  syncedTimeRange,
  onTimeRangeChange,
}) {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);

  const chartRef = useRef(null);
  const plSeriesRef = useRef(null);
  const idealSeriesRef = useRef(null);

  const dataRef = useRef([]);
  const isSyncingRef = useRef(false);

  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState(null);

  const pinnedRef = useRef(null);
  useEffect(() => {
    pinnedRef.current = pinned;
  }, [pinned]);

  // ✅ solta o tooltip fixo ao tocar fora do componente
  useEffect(() => {
    if (!pinned) return;

    const onDown = (e) => {
      const root = wrapperRef.current;
      if (!root) return;
      if (!root.contains(e.target)) setPinned(null);
    };

    window.addEventListener("pointerdown", onDown, { capture: true });
    window.addEventListener("touchstart", onDown, { capture: true });

    return () => {
      window.removeEventListener("pointerdown", onDown, { capture: true });
      window.removeEventListener("touchstart", onDown, { capture: true });
    };
  }, [pinned]);

  const COLORS = useMemo(
    () => ({
      grid: "rgba(255,255,255,0.045)",
      zeroLine: "rgba(255,255,255,0.18)",

      plPosLine: "rgba(120,255,170,0.95)",
      plNegLine: "rgba(120,255,170,0.95)",
      plPosFill: "rgba(120,255,170,0.06)",
      plNegFill: "rgba(255,120,120,0.06)",

      // ✅ ideal (linha por cima)
      idealLine: "rgba(181, 7, 146, 0.45)",

      tooltipBg: "rgba(10,10,12,0.88)",
      tooltipBorder: "rgba(255,255,255,0.14)",
      tooltipText: "rgba(255,255,255,0.92)",
    }),
    []
  );

  const fmtPct01Signed = (x, decimals = 2) => {
    if (x == null) return "—";
    const n = Number(x);
    if (!Number.isFinite(n)) return "—";
    const s = n > 0 ? "+" : n < 0 ? "−" : "";
    return `${s}${Math.abs(n * 100).toFixed(decimals)}%`;
  };

  const formatTime = (t) => {
    if (!t) return "";
    if (typeof t === "string") return t;
    const y = t.year;
    const m = String(t.month).padStart(2, "0");
    const d = String(t.day).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const normISO = (iso) => {
    if (!iso) return null;
    const s = String(iso).slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
  };

  // ======================
  // create chart (ONCE)
  // ======================
  useEffect(() => {
    const container = containerRef.current;
    if (!container || chartRef.current) return;

    const width = container.getBoundingClientRect().width || 600;

    const chart = createChart(container, {
      width,
      height: 120,
      layout: {
        background: { type: "solid", color: "rgba(10,10,12,0.0)" },
        textColor: "rgba(255,255,255,0.70)",
      },
      crosshair: { mode: 1 },
      grid: {
        vertLines: { color: COLORS.grid },
        horzLines: { color: COLORS.grid },
      },
      rightPriceScale: {
        visible: false,
        borderVisible: false,
        ticksVisible: false,
        scaleMargins: { top: 0.2, bottom: 0.1 },
      },
      leftPriceScale: { visible: false },
      timeScale: {
        borderVisible: false,
        ticksVisible: false,
        timeVisible: false,
        secondsVisible: false,
        rightOffset: 2,
        fixLeftEdge: true,
        fixRightEdge: true,
        visible: false,
      },
      handleScale: { axisPressedMouseMove: false },
    });

    const plSeries = chart.addSeries(BaselineSeries, {
      priceScaleId: "right",
      baseValue: { type: "price", price: 0 },
      topLineColor: COLORS.plPosLine,
      topFillColor1: COLORS.plPosFill,
      topFillColor2: COLORS.plPosFill,
      bottomLineColor: COLORS.plNegLine,
      bottomFillColor1: COLORS.plNegFill,
      bottomFillColor2: COLORS.plNegFill,
      lineWidth: 2,
      lastValueVisible: false,
      priceLineVisible: false,
      priceFormat: {
        type: "custom",
        formatter: (v) => `${Number(v).toFixed(2)}%`,
      },
    });

    plSeries.createPriceLine({
      price: 0,
      color: COLORS.zeroLine,
      lineWidth: 1,
      lineStyle: 2,
      axisLabelVisible: false,
      title: "",
    });

    const idealSeries = chart.addSeries(LineSeries, {
      priceScaleId: "right",
      color: COLORS.idealLine,
      lineWidth: 1.5,
      lineStyle: 3,
      lastValueVisible: false,
      priceLineVisible: false,
    });

    chartRef.current = chart;
    plSeriesRef.current = plSeries;
    idealSeriesRef.current = idealSeries;

    const handleResize = () => {
      if (!chartRef.current || !containerRef.current) return;
      const w = containerRef.current.getBoundingClientRect().width;
      if (w > 0) chartRef.current.applyOptions({ width: w });
    };
    window.addEventListener("resize", handleResize);

    const handleCrosshairMove = (param) => {
      if (pinnedRef.current) return;
      try {
        if (
          !param?.time ||
          !param?.point ||
          !wrapperRef.current ||
          param.point.x < 0 ||
          param.point.y < 0 ||
          param.point.x > wrapperRef.current.clientWidth ||
          param.point.y > wrapperRef.current.clientHeight
        ) {
          setHover(null);
          return;
        }
        const d = formatTime(param.time);
        setHover(dataRef.current.find((r) => r.date === d) || null);
      } catch {
        setHover(null);
      }
    };
    chart.subscribeCrosshairMove(handleCrosshairMove);

    const handleClick = (param) => {
      try {
        if (!param?.time) {
          setPinned(null);
          return;
        }
        const d = formatTime(param.time);
        setPinned(dataRef.current.find((r) => r.date === d) || null);
      } catch {
        setPinned(null);
      }
    };
    chart.subscribeClick(handleClick);

    const handleRangeChange = (range) => {
      if (!onTimeRangeChange || isSyncingRef.current) return;
      if (!range) return;
      onTimeRangeChange(range);
    };
    chart.timeScale().subscribeVisibleTimeRangeChange(handleRangeChange);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.unsubscribeCrosshairMove(handleCrosshairMove);
      chart.unsubscribeClick(handleClick);
      chart.timeScale().unsubscribeVisibleTimeRangeChange(handleRangeChange);

      chart.remove();
      chartRef.current = null;
      plSeriesRef.current = null;
      idealSeriesRef.current = null;
    };
  }, [COLORS, onTimeRangeChange]);

  // ======================
  // set data (✅ eixo X continua; curva só após openedDateISO)
  // ======================
  useEffect(() => {
    if (!chartRef.current || !plSeriesRef.current || !idealSeriesRef.current) return;

    const base = Array.isArray(data) ? data : [];
    const merged = [...base];

    if (todayPoint?.date) {
      const td = String(todayPoint.date).slice(0, 10);
      const idx = merged.findIndex((x) => String(x?.date).slice(0, 10) === td);
      const row = { ...todayPoint, date: td };
      if (idx >= 0) merged[idx] = { ...merged[idx], ...row };
      else merged.push(row);
    }

    const opened = normISO(openedDateISO);

    // ✅ mantém TODAS as datas do período (não filtra!)
    // ✅ antes do openedDate: vira "whitespace" (sem value) => curva não aparece
    const clean = merged
      .filter((p) => p?.date)
      .map((p) => {
        const date = String(p.date).slice(0, 10);

        let pct01 = p?.pct01 != null ? Number(p.pct01) : null;
        let ideal_pct01 = p?.ideal_pct01 != null ? Number(p.ideal_pct01) : null;

        pct01 = Number.isFinite(pct01) ? pct01 : null;
        ideal_pct01 = Number.isFinite(ideal_pct01) ? ideal_pct01 : null;

        const isBeforeOpen = opened && date < opened;

        return {
          date,
          pct01: isBeforeOpen ? null : pct01,
          ideal_pct01: isBeforeOpen ? null : ideal_pct01,
        };
      })
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

    dataRef.current = clean;

    // ✅ REAL baseline: whitespace antes de abrir
    const plData = clean.map((p) =>
      Number.isFinite(p.pct01)
        ? { time: p.date, value: p.pct01 * 100 }
        : { time: p.date } // whitespace
    );
    plSeriesRef.current.setData(plData);

    // ✅ IDEAL line: whitespace antes de abrir
    const idealData = clean.map((p) =>
      Number.isFinite(p.ideal_pct01)
        ? { time: p.date, value: p.ideal_pct01 * 100 }
        : { time: p.date } // whitespace
    );
    idealSeriesRef.current.setData(idealData);

    // ✅ IMPORTANTE:
    // Se o pai NÃO estiver mandando syncedTimeRange, fitContent vai colapsar no range com dados.
    // Então só dá fitContent quando não há syncedTimeRange *e* você quer auto-fit.
    if (!syncedTimeRange) chartRef.current.timeScale().fitContent();
  }, [data, todayPoint, openedDateISO, syncedTimeRange]);

  // apply synced range (opcional) — isso é o que mantém o eixo X no período selecionado
  useEffect(() => {
    if (!chartRef.current || !syncedTimeRange) return;
    const ts = chartRef.current.timeScale();
    isSyncingRef.current = true;
    ts.setVisibleRange(syncedTimeRange);
    isSyncingRef.current = false;
  }, [syncedTimeRange]);

  const tip = pinned || hover;

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%", height: "100%" }}>
      {title ? (
        <div
          style={{
            position: "absolute",
            top: 6,
            left: 0,
            right: 0,
            textAlign: "center",
            fontSize: 12,
            fontWeight: 900,
            color: "rgba(255,255,255,0.88)",
            pointerEvents: "none",
            zIndex: 3,
            textShadow: "0 6px 18px rgba(0,0,0,0.55)",
          }}
        >
          {title}
        </div>
      ) : null}

      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />

      {tip?.date ? (
        <div
          style={{
            position: "absolute",
            top: 28,
            left: 8,
            padding: "7px 9px",
            fontSize: 11,
            background: COLORS.tooltipBg,
            color: COLORS.tooltipText,
            border: `1px solid ${COLORS.tooltipBorder}`,
            borderRadius: 12,
            boxShadow: "0 10px 26px rgba(0,0,0,0.35)",
            backdropFilter: "blur(8px)",
            pointerEvents: "none",
            lineHeight: 1.35,
            zIndex: 4,
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 4 }}>
            {tip.date} {pinned ? <span style={{ opacity: 0.7 }}>(fixo)</span> : null}
          </div>

          <div>
            Real: <b>{tip.pct01 != null ? fmtPct01Signed(tip.pct01, 2) : "—"}</b>
          </div>
          <div>
            Finbot α: <b>{tip.ideal_pct01 != null ? fmtPct01Signed(tip.ideal_pct01, 2) : "—"}</b>
          </div>

          {pinned ? (
            <div style={{ marginTop: 4, opacity: 0.7 }}>
              Toque fora do gráfico para soltar
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
